import React, { FC, useState, useEffect } from 'react'
import { ScrollView, TouchableOpacity, View, Text } from 'react-native'
import { useNavigate, useParams } from 'react-router-dom'
import { travelApi, TravelLogic, type ITravelData } from '@front/logic'

import {
  AppBackground,
  BottomActionContainer,
  Button,
  ResponsiveLogo,
} from '@ui/components'

import {
  Headline2,
  Headline5,
  BodyText,
  colors
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as PlaneIcon } from '@/assets/icons/paw_color.svg'
import { ReactComponent as DocumentIcon } from '@/assets/icons/paw_color.svg'
import { ReactComponent as EditIcon } from '@/assets/icons/paw_color.svg'
import { ReactComponent as DeleteIcon } from '@/assets/icons/paw_color.svg'
import { HeaderWithBackAena } from '@/components/headerwithback'

export const TravelDetail: FC = () => {
  const navigate = useNavigate()
  const { travelId } = useParams<{ travelId: string }>()

  const [travel, setTravel] = useState<ITravelData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    const loadTravelDetail = async () => {
      if (!travelId) return

      try {
        setLoading(true)
        setError(null)
        const response = await travelApi.getTravelDetail({ travelId })
        setTravel(response.data)
      } catch (err) {
        console.error('Error loading travel detail:', err)
        setError('Error al cargar los detalles del viaje')
      } finally {
        setLoading(false)
      }
    }

    loadTravelDetail()
  }, [travelId])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#28a745'
      case 'upcoming':
        return '#007bff'
      case 'cancelled':
        return '#dc3545'
      default:
        return '#6c757d'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completado'
      case 'upcoming':
        return 'Próximo'
      case 'cancelled':
        return 'Cancelado'
      default:
        return 'Desconocido'
    }
  }

  const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <View style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#f0f0f0'
    }}>
      <BodyText style={{ color: '#666', fontWeight: '500' }}>{label}</BodyText>
      <BodyText style={{ fontWeight: '600', textAlign: 'right', flex: 1, marginLeft: 16 }}>
        {value}
      </BodyText>
    </View>
  )

  const PetCard: React.FC<{ pet: ITravelData['pets'][0] }> = ({ pet }) => (
    <View style={{
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#f8f9fa',
      padding: 12,
      borderRadius: 8,
      marginBottom: 8,
      borderLeftWidth: 4,
      borderLeftColor: pet.travelType === 'Cabina' ? '#28a745' : '#007bff'
    }}>
      <Text style={{ fontSize: 32, marginRight: 12 }}>🐕</Text>
      <View style={{ flex: 1 }}>
        <BodyText style={{ fontWeight: '600', fontSize: 16 }}>
          {pet.name}
        </BodyText>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
          <PlaneIcon width={12} height={12} fill="#dc3545" style={{ marginRight: 4 }} />
          <BodyText style={{ color: '#666', fontSize: 14 }}>
            {pet.travelType}
          </BodyText>
        </View>
      </View>
    </View>
  )

 
  const handleEdit = () => {
    if (travel) {
      navigate(`${travel.id}/edit`)
    }
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!travel) return

    try {
      await travelApi.deleteTravel(travel.id)
      setShowDeleteConfirm(false)
      navigate('aena/travel/list')
    } catch (error) {
      console.error('Error deleting travel:', error)
      alert('Error al eliminar el viaje. Inténtalo de nuevo.')
    }
  }

  const handleViewDocuments = () => {
    if (travel) {
      navigate(`aena/travel/detail/${travel.id}/documents`)
    }
  }

  if (loading) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Cargando detalles del viaje...</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (error) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <BodyText style={{ textAlign: 'center', marginBottom: 16, color: '#dc3545' }}>
            {error}
          </BodyText>
          <TouchableOpacity
            onPress={() => navigate('aena/travel/list')}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              backgroundColor: '#96ce00',
              alignItems: 'center'
            }}
          >
            <BodyText style={{ color: '#1a2732', fontWeight: '600' }}>Volver a la lista</BodyText>
          </TouchableOpacity>
        </View>
      </AppBackground>
    )
  }

  if (!travel) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Viaje no encontrado</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (showDeleteConfirm) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            alignItems: 'center',
            minWidth: 300,
            maxWidth: 400
          }}>
            <Headline2 style={{ marginBottom: 16, textAlign: 'center', color: '#dc3545' }}>
              Confirmar eliminación
            </Headline2>

            <BodyText style={{ textAlign: 'center', marginBottom: 24, color: '#666' }}>
              ¿Estás seguro de que quieres eliminar este viaje? Esta acción no se puede deshacer.
            </BodyText>

            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity
                onPress={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  paddingHorizontal: 20,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: '#ccc',
                  alignItems: 'center'
                }}
              >
                <BodyText style={{ color: '#666' }}>Cancelar</BodyText>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={confirmDelete}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  paddingHorizontal: 20,
                  borderRadius: 8,
                  backgroundColor: '#dc3545',
                  alignItems: 'center'
                }}
              >
                <BodyText style={{ color: 'white', fontWeight: '600' }}>Eliminar</BodyText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </AppBackground>
    )
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena
          title={`${travel.departureAirport.split(' ')[0]} ${travel.arrivalAirport.split(' ')[0]}`}
        />

        <BottomActionContainer
          bottomAction={
            <Button
              title="Ver documentos"
              icon={<DocumentIcon width={24} height={24} fill="#1a2732" />}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
              allowIconAndText
              color="primary"
              onPress={handleViewDocuments}
            />
          }
        >
          <ScrollView style={{ padding: 16 }}>
            {/* Header con fecha y estado */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16
              }}>
                <View style={{
                  backgroundColor: '#dc3545',
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 16
                }}>
                  <Text style={{ color: 'white', fontSize: 14, fontWeight: 'bold' }}>
                    Fecha: {TravelLogic.formatDate(travel.flightDate)}
                  </Text>
                </View>

                <View style={{
                  backgroundColor: getStatusColor(travel.status),
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 12
                }}>
                  <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                    {getStatusText(travel.status)}
                  </Text>
                </View>
              </View>

              {/* Acciones */}
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                <TouchableOpacity
                  onPress={handleEdit}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#007bff',
                    backgroundColor: '#f8f9ff'
                  }}
                >
                  <EditIcon width={16} height={16} fill="#007bff" style={{ marginRight: 6 }} />
                  <BodyText style={{ color: '#007bff', fontWeight: '600' }}>Editar</BodyText>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleDelete}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#dc3545',
                    backgroundColor: '#fff8f8'
                  }}
                >
                  <DeleteIcon width={16} height={16} fill="#dc3545" style={{ marginRight: 6 }} />
                  <BodyText style={{ color: '#dc3545', fontWeight: '600' }}>Eliminar</BodyText>
                </TouchableOpacity>
              </View>
            </View>

            {/* Información del vuelo */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                Información del Vuelo
              </Headline5>

              <InfoRow label="Compañía" value={travel.airline} />
              <InfoRow label="Nº de vuelo" value={travel.flightNumber} />
              <InfoRow label="Localizador" value={travel.locator} />
              <InfoRow label="A.Salida" value={travel.departureAirport} />
              <InfoRow label="A.Destino" value={travel.arrivalAirport} />
            </View>

            {/* Pasajeros (Mascotas) */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                Pasajeros
              </Headline5>

              {travel.pets.map(pet => (
                <PetCard key={pet.guid} pet={pet} />
              ))}
            </View>

            {/* Información del propietario */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                Información del Propietario
              </Headline5>

              <InfoRow label="Nombre" value={travel.ownerName} />
              <InfoRow label="Teléfono" value={travel.phoneNumber} />
              <InfoRow label="Email" value={travel.contactEmail} />
            </View>
          </ScrollView>
        </BottomActionContainer>
      </View>
    </AppBackground>
  )
}

export default TravelDetail
